import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Typography, Spin, message, Grid } from "antd";
import { StarOutlined } from "@ant-design/icons";
import { useLocation, useHistory } from "react-router-dom";
import "./marketSearchPage.css";
import FilterBar from "./components/FilterBar";
import KpiStrip from "./components/KpiStrip";
import Panel from "./components/Panel";
import ScatterChart from "./components/ScatterChart";
import VehicleSummaryCard from "./components/VehicleSummaryCard";
import ActiveCompsTab from "./components/ActiveCompsTab";
import SoldCompsTab from "./components/SoldCompsTab";
import ActiveCompsMap from "./components/ActiveCompsMap";
import MarketOverviewCards from "./components/MarketOverviewCards";
import MarketStrategyInsightsCard from "./components/MarketStrategyInsightsCard";
import CompetitiveMarketProfile from "./components/CompetitiveMarketProfile";
import AddedToWatchlistAlert from "./components/AddedToWatchlistAlert";
import RemovedFromWatchlistAlert from "./components/RemovedFromWatchlistAlert";
import { addToWatchlist, removeFromWatchlist, fetchWatchlist } from "./services/api";

const { Title } = Typography;
const { useBreakpoint } = Grid;

export default function MarketSearchPage() {
  const location = useLocation();
  const history = useHistory();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [selectedPeriod, setSelectedPeriod] = useState("90D");
  const [activeTab, setActiveTab] = useState("overview");
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWatchlistAlert, setShowWatchlistAlert] = useState(false);
  const [showRemovedAlert, setShowRemovedAlert] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [isInWatchlist, setIsInWatchlist] = useState(false);

  // Metrics for mobile stats card (same logic as KpiStrip, no API change)
  const activeListings = vehicleData?.active_listings || vehicleData?.market_analysis?.active_listings || [];
  const recentSales = vehicleData?.market_analysis?.recent_sales || [];
  const filterByPeriod = useMemo(() => {
    const now = new Date();
    let daysAgo = selectedPeriod === "60D" ? 60 : selectedPeriod === "30D" ? 30 : 90;
    const cutoff = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
    return (dateString) => dateString && new Date(dateString) >= cutoff;
  }, [selectedPeriod]);
  const mobileActiveCount = useMemo(
    () => activeListings.filter((l) => (l.distance_miles ?? l.distance_mi ?? l.distance) == null || (l.distance_miles ?? l.distance_mi ?? l.distance) <= 100).length,
    [activeListings]
  );
  const mobileSoldCount = useMemo(
    () => recentSales.filter((s) => filterByPeriod(s.last_seen_at_date || s.sold_date)).length,
    [recentSales, filterByPeriod]
  );
  const periodDays = selectedPeriod === "30D" ? 30 : selectedPeriod === "60D" ? 60 : 90;
  const mobileMds = useMemo(() => {
    if (mobileSoldCount <= 0) return null;
    return Math.round(mobileActiveCount / (mobileSoldCount / periodDays));
  }, [mobileActiveCount, mobileSoldCount, periodDays]);
  const mobileAvgDom = useMemo(() => {
    const filtered = recentSales.filter((s) => filterByPeriod(s.last_seen_at_date || s.sold_date));
    if (filtered.length === 0) return null;
    return Math.round(filtered.reduce((sum, s) => sum + (s.dom_active || s.dom || 0), 0) / filtered.length);
  }, [recentSales, filterByPeriod]);

  // Clear data on mount to ensure page starts empty
  useEffect(() => {
    setVehicleData(null);
    localStorage.removeItem('hooptie_vehicle_data');
  }, []);

  // Clear vehicle data only when URL params change to a *different* VIN (e.g. user clicked another watchlist item).
  // Do not clear when URL is updated after a successful search (same VIN) or we'd wipe the result and force a retry.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlVin = urlParams.get('vin')?.trim().toUpperCase();
    if (!urlVin) return;
    setVehicleData((prev) => {
      const currentVin = prev?.vehicle_info?.vin?.trim().toUpperCase() ?? prev?.search_metadata?.vin?.trim().toUpperCase();
      if (currentVin && urlVin !== currentVin) return null;
      return prev;
    });
  }, [location.search]);

  // Enable scrolling on this page except on Appraisal tab (appraisal should not scroll)
  useEffect(() => {
    const contentEl = document.querySelector('.content-ant.main-content');
    if (!contentEl) return;
    if (activeTab === 'appraisal') {
      contentEl.classList.remove('market-search-scrollable');
      contentEl.style.setProperty('overflow-y', 'hidden', 'important');
      contentEl.style.setProperty('overflow-x', 'hidden', 'important');
      contentEl.style.setProperty('height', 'calc(100vh - 40px)', 'important');
    } else {
      contentEl.classList.add('market-search-scrollable');
      contentEl.style.setProperty('overflow-y', 'auto', 'important');
      contentEl.style.setProperty('overflow-x', 'hidden', 'important');
      contentEl.style.setProperty('height', 'calc(100vh - 40px)', 'important');
      contentEl.style.setProperty('min-height', '100%', 'important');
    }
    return () => {
      contentEl.classList.remove('market-search-scrollable');
      contentEl.style.removeProperty('overflow-y');
      contentEl.style.removeProperty('overflow-x');
      contentEl.style.removeProperty('height');
      contentEl.style.removeProperty('min-height');
    };
  }, [activeTab]);

  const handleDataReceived = useCallback((data) => {
    console.log('MarketSearchPage: Data received', data);
    if (data) {
      setVehicleData(data);
      setLoading(false);
    } else {
      // Only clear data if explicitly null (not undefined)
      if (data === null) {
        setVehicleData(null);
      }
    }
  }, []);

  // Check if current vehicle is in watchlist
  useEffect(() => {
    const checkWatchlistStatus = async () => {
      if (!vehicleData?.vehicle_info?.vin) {
        setIsInWatchlist(false);
        setWatchlistItems([]);
        return;
      }

      try {
        const response = await fetchWatchlist();
        if (response?.data && Array.isArray(response.data)) {
          const currentVin = vehicleData.vehicle_info.vin?.trim().toUpperCase();
          // Match based on VIN (case-insensitive, trimmed)
          const isInList = response.data.some(item => {
            const itemVin = item.vin?.trim().toUpperCase();
            return itemVin && itemVin === currentVin;
          });
          setIsInWatchlist(isInList);
          setWatchlistItems(response.data);
        } else {
          setIsInWatchlist(false);
          setWatchlistItems([]);
        }
      } catch (error) {
        console.error('Error checking watchlist:', error);
        setIsInWatchlist(false);
        setWatchlistItems([]);
      }
    };

    checkWatchlistStatus();
  }, [vehicleData?.vehicle_info?.vin]);

  const handleWatchlistToggle = useCallback(async () => {
    if (!vehicleData?.vehicle_info) {
      message.error('Vehicle information is not available.');
      return;
    }

    const vehicleInfo = vehicleData.vehicle_info;
    const vin = vehicleInfo.vin;

    if (!vin) {
      message.error('VIN is required.');
      return;
    }

    try {
      setWatchlistLoading(true);
      
      if (isInWatchlist) {
        // Remove from watchlist
        await removeFromWatchlist(vin);
        setIsInWatchlist(false);
        setShowRemovedAlert(true);
        // Update watchlist items
        const response = await fetchWatchlist();
        if (response?.data && Array.isArray(response.data)) {
          setWatchlistItems(response.data);
        }
        // Dispatch event to refresh sidenav watchlist
        window.dispatchEvent(new CustomEvent('watchlistUpdated'));
      } else {
        // Add to watchlist
        // Extract required fields
        const year = vehicleInfo.year;
        const make = vehicleInfo.make;
        const model = vehicleInfo.model;
        const trim = vehicleInfo.trim;

        // Validate required fields
        if (!year || !make || !model || !trim) {
          message.error('Missing required vehicle information (year, make, model, or trim).');
          return;
        }

        // Extract optional fields
        const version = vehicleInfo.version || null;
        const mileage = vehicleInfo.mileage || vehicleData?.search_metadata?.mileage || null;
        
        // Extract overall_grade - same logic as VehicleSummaryCard
        const rawOverall =
          vehicleData?.market_analysis?.ai_market_insights?.overall_grade ??
          vehicleData?.ai_market_insights?.overall_grade;
        const overallGrade =
          rawOverall && typeof rawOverall === "object" && "overall_grade" in rawOverall
            ? rawOverall.overall_grade
            : typeof rawOverall === "string"
              ? rawOverall
              : null;

        // Build payload
        const payload = {
          year: parseInt(year, 10),
          make: make,
          model: model,
          trim: trim,
        };

        // Add optional fields only if they exist
        if (version) payload.version = version;
        if (vin) payload.vin = vin;
        if (mileage) payload.mileage = parseInt(mileage, 10);
        if (overallGrade) payload.overall_grade = overallGrade;

        await addToWatchlist(payload);
        setIsInWatchlist(true);
        setShowWatchlistAlert(true);
        // Update watchlist items
        const response = await fetchWatchlist();
        if (response?.data && Array.isArray(response.data)) {
          setWatchlistItems(response.data);
        }
        // Dispatch event to refresh sidenav watchlist
        window.dispatchEvent(new CustomEvent('watchlistUpdated'));
      }
    } catch (error) {
      console.error('Error toggling watchlist:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 
        (isInWatchlist ? 'Failed to remove from watchlist. Please try again.' : 'Failed to add to watchlist. Please try again.');
      message.error(errorMessage);
    } finally {
      setWatchlistLoading(false);
    }
  }, [vehicleData, isInWatchlist]);

  const handleChangeVehicle = useCallback(() => {
    setVehicleData(null);
    history.replace(location.pathname);
  }, [history, location.pathname]);

  // If no vehicle data, show only search bars
  if (!vehicleData) {
    return (
      <div className="market-search-page">
        {/* Market Search Title - hidden on mobile */}
        <div className="market-search-page-title" style={{ marginTop: '0px', marginBottom: '12px', paddingTop: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Title 
            level={1} 
            style={{ 
              color: '#ffffff', 
              marginBottom: 0,
              fontWeight: 700,
              fontSize: '20px',
              marginTop: 0,
              marginLeft: '0',
              paddingTop: 0,
              lineHeight: 1.3,
              overflow: 'visible'
            }}
          >
            Market Search
          </Title>
        </div>

        {/* Top Filter Bar */}
        <div className="ms-filter-container">
          <FilterBar 
            onDataReceived={handleDataReceived}
            onLoadingChange={setLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="market-search-page">
      {/* Market Search Title - hidden on mobile */}
      <div className="market-search-page-title" style={{ marginTop: '0px', marginBottom: '12px', paddingTop: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Title 
          level={1} 
          style={{ 
            color: '#ffffff', 
            marginBottom: 0,
            fontWeight: 700,
            fontSize: '20px',
            marginTop: 0,
            marginLeft: '0',
            paddingTop: 0,
            lineHeight: 1.3,
            overflow: 'visible'
          }}
        >
          Market Search
        </Title>
      </div>

      {/* Top Filter Bar (mobile: shows search form OR vehicle summary in same card) */}
      <div className="ms-filter-container">
        <FilterBar 
          onDataReceived={handleDataReceived}
          onLoadingChange={setLoading}
          vehicleData={isMobile ? vehicleData : null}
          onChangeVehicle={isMobile ? handleChangeVehicle : undefined}
        />
      </div>

      {/* Main Tabs + Add to Watchlist (desktop) or Mobile layout */}
      {isMobile ? (
        <div className="ms-mobile-container">
          <div className="ms-mobile-tabs-wrap">
            <div className="ms-mobile-tabs">
              <button
                className={`ms-mobile-tab ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <span className="ms-mobile-tab-divider" />
              <button
                className={`ms-mobile-tab ${activeTab === "active" ? "active" : ""}`}
                onClick={() => setActiveTab("active")}
              >
                Active
              </button>
              <span className="ms-mobile-tab-divider" />
              <button
                className={`ms-mobile-tab ${activeTab === "sold" ? "active" : ""}`}
                onClick={() => setActiveTab("sold")}
              >
                Sold
              </button>
              <span className="ms-mobile-tab-divider" />
              <button
                className={`ms-mobile-tab ${activeTab === "appraisal" ? "active" : ""}`}
                onClick={() => setActiveTab("appraisal")}
              >
                Appraisal
              </button>
            </div>
          </div>

          {loading ? (
            <div className="ms-mobile-loading">
              <Spin size="large" />
              <div className="ms-mobile-loading-text">Loading market data...</div>
            </div>
          ) : activeTab === "overview" ? (
            <div className="ms-mobile-overview">
              <div className="ms-period-filter-pill">
                <button type="button" className={`ms-period-option ${selectedPeriod === "30D" ? "active" : ""}`} onClick={() => setSelectedPeriod("30D")}>30D</button>
                <button type="button" className={`ms-period-option ${selectedPeriod === "60D" ? "active" : ""}`} onClick={() => setSelectedPeriod("60D")}>60D</button>
                <button type="button" className={`ms-period-option ${selectedPeriod === "90D" ? "active" : ""}`} onClick={() => setSelectedPeriod("90D")}>90D</button>
              </div>
              <div className="msStatsCard">
                <div className="msStatsGrid">
                  <div className="msStatCell">
                    <span className="msStatLabel">Active:</span>
                    <span className="msStatValue">{mobileActiveCount ?? "—"}</span>
                  </div>
                  <div className="msStatCell msRight">
                    <span className="msStatLabel">Sold:</span>
                    <span className="msStatValue">{mobileSoldCount ?? "—"}</span>
                  </div>
                  <div className="msStatCell msBottom">
                    <span className="msStatLabel">DOM:</span>
                    <span className="msStatValue">{mobileAvgDom != null ? mobileAvgDom : "—"}</span>
                    <span className="msStatSuffix">days</span>
                  </div>
                  <div className="msStatCell msRight msBottom">
                    <span className="msStatLabel">MDS:</span>
                    <span className="msStatValue">{mobileMds != null ? mobileMds : "—"}</span>
                    <span className="msStatSuffix">days</span>
                  </div>
                  <div className="msDividerV" />
                  <div className="msDividerH" />
                </div>
              </div>
              <h2 className="ms-mobile-section-title">Market Insights</h2>
              <div className="ms-mobile-insights">
                <MarketStrategyInsightsCard data={vehicleData} mobile />
              </div>
            </div>
          ) : activeTab === "active" ? (
            <ActiveCompsTab data={vehicleData} isMobile={true} />
          ) : activeTab === "sold" ? (
            <SoldCompsTab data={vehicleData} isMobile={true} />
          ) : (
            <CompetitiveMarketProfile data={vehicleData} mobile={true} />
          )}
        </div>
      ) : (
        <>
          <div className="ms-main-tabs">
            <div className="ms-main-tabs-group">
              <button
                className={`ms-main-tab ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                className={`ms-main-tab ${activeTab === "active" ? "active" : ""}`}
                onClick={() => setActiveTab("active")}
              >
                Active Comps
              </button>
              <button
                className={`ms-main-tab ${activeTab === "sold" ? "active" : ""}`}
                onClick={() => setActiveTab("sold")}
              >
                Sold Comps
              </button>
              <button
                className={`ms-main-tab ${activeTab === "appraisal" ? "active" : ""}`}
                onClick={() => setActiveTab("appraisal")}
              >
                Appraisal
              </button>
            </div>
            <button
              type="button"
              className={`ms-watchlist-btn ${isInWatchlist ? 'ms-watchlist-btn-remove' : ''}`}
              onClick={handleWatchlistToggle}
              disabled={watchlistLoading || !vehicleData?.vehicle_info}
            >
              <StarOutlined className="ms-watchlist-btn-icon" />
              {watchlistLoading 
                ? (isInWatchlist ? 'Removing...' : 'Adding...') 
                : (isInWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist')}
            </button>
          </div>

          {/* Main Content */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ffffff' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#8ea0c6' }}>Loading market data...</div>
            </div>
          ) : (
            <>
              {activeTab === "overview" && (
                <div className="ms-content-grid">
              {/* Left Column (70%) */}
              <div className="ms-left-column">
                {/* Time Period Filter */}
                <div className="ms-period-filter">
                  <div className="ms-period-label">Time Period:</div>
                  <div className="ms-period-buttons">
                    <button
                      className={`ms-period-button ${selectedPeriod === "30D" ? "active" : ""}`}
                      onClick={() => setSelectedPeriod("30D")}
                    >
                      30D
                    </button>
                    <button
                      className={`ms-period-button ${selectedPeriod === "60D" ? "active" : ""}`}
                      onClick={() => setSelectedPeriod("60D")}
                    >
                      60D
                    </button>
                    <button
                      className={`ms-period-button ${selectedPeriod === "90D" ? "active" : ""}`}
                      onClick={() => setSelectedPeriod("90D")}
                    >
                      90D
                    </button>
                  </div>
                </div>

                {/* Market Summary Card */}
                <Panel>
                  <KpiStrip selectedPeriod={selectedPeriod} data={vehicleData} />
                </Panel>

                {/* Market Strategy Insights Card */}
                <MarketStrategyInsightsCard selectedPeriod={selectedPeriod} data={vehicleData} />

                {/* Scatter Chart Panel */}
                <Panel title="Price vs Mileage Scatter Chart" showSettings={true}>
                  <ScatterChart selectedPeriod={selectedPeriod} data={vehicleData} />
                </Panel>

                {/* Competition Heat Map Panel */}
                <Panel title="Competition Heat Map" showSettings={true}>
                  <ActiveCompsMap
                    activeListings={vehicleData?.active_listings || vehicleData?.market_analysis?.active_listings || []}
                    dealerLat={vehicleData?.search_metadata?.dealer_latitude ?? vehicleData?.search_metadata?.dealer_lat}
                    dealerLng={vehicleData?.search_metadata?.dealer_longitude ?? vehicleData?.search_metadata?.dealer_lng}
                    hooptiePrice={vehicleData?.pricing_info?.marketcheck_price || vehicleData?.pricing_info?.hooptie_price || vehicleData?.search_metadata?.asking_price || 0}
                    targetMiles={vehicleData?.search_metadata?.mileage || vehicleData?.vehicle_info?.mileage || 0}
                    marketAvgDom={vehicleData?.market_analysis?.market_metrics?.avg_days_on_market || 0}
                  />
                </Panel>

                {/* Market Overview Cards */}
                <Panel>
                  <MarketOverviewCards
                    targetMileage={vehicleData?.search_metadata?.mileage || vehicleData?.vehicle_info?.mileage || 0}
                    active_listings={vehicleData?.active_listings || vehicleData?.market_analysis?.active_listings || []}
                    sold_listings={vehicleData?.market_analysis?.recent_sales || []}
                    selectedPeriod={selectedPeriod}
                  />
                </Panel>
              </div>

              {/* Right Column (30%) */}
              <div className="ms-right-column">
                <VehicleSummaryCard data={vehicleData} />
              </div>
            </div>
          )}

          {activeTab === "active" && (
            <ActiveCompsTab data={vehicleData} />
          )}

          {activeTab === "sold" && (
            <SoldCompsTab data={vehicleData} />
          )}

          {activeTab === "appraisal" && (
            <CompetitiveMarketProfile data={vehicleData} />
          )}
            </>
          )}
        </>
      )}

      <AddedToWatchlistAlert
        open={showWatchlistAlert}
        onClose={() => setShowWatchlistAlert(false)}
        vehicleName={
          vehicleData?.vehicle_info
            ? [
                vehicleData.vehicle_info.year,
                vehicleData.vehicle_info.make,
                vehicleData.vehicle_info.model,
                vehicleData.vehicle_info.trim,
              ]
                .filter(Boolean)
                .join(" ")
            : null
        }
      />

      <RemovedFromWatchlistAlert
        open={showRemovedAlert}
        onClose={() => setShowRemovedAlert(false)}
        vehicleName={
          vehicleData?.vehicle_info
            ? [
                vehicleData.vehicle_info.year,
                vehicleData.vehicle_info.make,
                vehicleData.vehicle_info.model,
                vehicleData.vehicle_info.trim,
              ]
                .filter(Boolean)
                .join(" ")
            : null
        }
      />
    </div>
  );
}
